import { useEffect, useRef, useState } from "react";
import {
  Box3,
  Color,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector3,
  WebGLRenderer
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import type { DeviceModelDefinition } from "../models/device-model-catalog";
import { CadDevice } from "../objects/cad-device";

export function DeviceModelPreview({ model }: { model?: DeviceModelDefinition }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !model) {
      return;
    }

    let disposed = false;
    let frameId: number | undefined;
    const scene = new Scene();
    scene.background = new Color("#0a0d10");
    const renderer = new WebGLRenderer({ antialias: true });
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(Math.max(host.clientWidth, 1), Math.max(host.clientHeight, 1));
    host.appendChild(renderer.domElement);

    const camera = new PerspectiveCamera(
      42,
      Math.max(host.clientWidth, 1) / Math.max(host.clientHeight, 1),
      0.01,
      1000
    );
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    scene.add(new HemisphereLight("#ffffff", "#30343a", 2.2));
    const keyLight = new DirectionalLight("#ffffff", 3.2);
    keyLight.position.set(6, 9, 7);
    scene.add(keyLight);

    const render = () => {
      if (!disposed) {
        renderer.render(scene, camera);
      }
    };
    const requestRender = () => {
      if (frameId !== undefined || disposed) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = undefined;
        render();
      });
    };
    controls.addEventListener("change", requestRender);

    const resizeObserver = new ResizeObserver(() => {
      const width = Math.max(host.clientWidth, 1);
      const height = Math.max(host.clientHeight, 1);
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      requestRender();
    });
    resizeObserver.observe(host);

    setError(undefined);
    setLoading(true);
    void CadDevice.create(
      {
        blockPrefix: model.blockPrefix,
        id: "preview",
        mirrorX: false,
        mirrorY: false,
        mirrorZ: false,
        positionX: 0,
        positionZ: 0,
        rotationY: 0
      },
      model
    )
      .then((device) => {
        if (disposed) {
          disposePreviewObject(device);
          return;
        }
        scene.add(device);
        framePreviewCamera(camera, controls, device);
        setLoading(false);
        requestRender();
      })
      .catch((loadError: unknown) => {
        if (!disposed) {
          setLoading(false);
          setError(loadError instanceof Error ? loadError.message : "设备模型预览失败");
        }
      });

    return () => {
      disposed = true;
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      controls.removeEventListener("change", requestRender);
      controls.dispose();
      scene.children.forEach((child) => {
        disposePreviewObject(child);
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [model]);

  return (
    <div className="device-model-preview">
      <div className="device-model-preview-host" ref={hostRef} />
      {!model ? <div className="device-model-preview-state">选择一个型号进行预览</div> : null}
      {loading ? <div className="device-model-preview-state">正在加载模型...</div> : null}
      {error ? (
        <div className="device-model-preview-state" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function framePreviewCamera(camera: PerspectiveCamera, controls: OrbitControls, target: CadDevice) {
  target.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(target);
  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const radius = Math.max(size.x, size.y, size.z, 0.1);
  camera.position.copy(center).add(new Vector3(radius * 1.3, radius * 0.9, radius * 1.3));
  camera.near = Math.max(radius / 100, 0.01);
  camera.far = Math.max(radius * 20, 100);
  camera.updateProjectionMatrix();
  controls.target.copy(center);
  controls.update();
}

function disposePreviewObject(object: import("three").Object3D) {
  object.traverse((child) => {
    if (!("geometry" in child) || !("material" in child)) {
      return;
    }
    const renderable = child as import("three").Mesh;
    renderable.geometry.dispose();
    const materials = Array.isArray(renderable.material)
      ? renderable.material
      : [renderable.material];
    materials.forEach((material) => {
      material.dispose();
    });
  });
}
